import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren } from '@angular/core';
import {
  Command,
  CommandStore,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS } from '@softline/application';
import {
  Change,
  CreateChange,
  DateService,
  FileService,
  isDefined,
  RequestError,
  parseIso8601,
  toIsoDate,
  Store } from '@softline/core';
import {
  MessageBarStore,
  ModalStore,
  NumberInputComponent,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PrintPayload } from '../../data/print-payload';
import { Product } from '../../data/product';
import { PrintConfigDialogComponent } from '../../dialogs/print-config-dialog/print-config-dialog.component';
import { SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE } from '../../kunde-konditionsliste.shared';
import { KundenKonditionslisteStore } from '../../store/kunden-konditionsliste.store';
import { FormSubmit, FormValue, HeaderComponent } from '../header/header.component';

const DATE_FORMAT = 'YYYY-MM-DD';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'lib-list-products',
  imports: [CommonModule, HeaderComponent,
    InputComponent,
    NumberInputComponent,
    TranslatePipe],
  templateUrl: './list-products.component.html',
  styleUrls: ['./list-products.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush })
export class ListProductsComponent implements OnInit, OnDestroy {
  private readonly dateService = inject(DateService);
  private readonly store = inject(Store);
  private readonly fileService = inject(FileService);

  private formValue?: FormValue;

  @ViewChildren('num') private inputs?: QueryList<NumberInputComponent>;

  readonly selected$ = new BehaviorSubject<{ id: number | string | null }>({ id: null });

  readonly products$: Observable<Product[]> = this.store.observe(
    SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
    KundenKonditionslisteStore.getters.all
  );

  readonly changedProducts$: Observable<Change<Product>[]> = this.store.observe(
    SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
    KundenKonditionslisteStore.getters.changes
  );

  private static formatDate(stringValue: string): string | undefined {
    const time = parseIso8601(stringValue);
    return time === null ? undefined : toIsoDate(new Date(time));
  }

  async ngOnInit(): Promise<void> {
    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: ListProductsComponent,
      commands: this.createCommands() });
    await this.loadProducts(this.dateService.now()); //TODO: test
  }

  async ngOnDestroy(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      ListProductsComponent
    );
    await this.store.dispatch(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.actions.cancelAll
    );
  }

  protected createCommands(): Command[] {
    return [
      {
        name: 'Drucken',
        icon: 'fa-regular fa-print',
        class: 'menu top-menu top-menu-right',
        execute: async () => {
          await this.showPrintConfigDialog({
            valid_from: this.formValue?.validFrom ?? this.dateService.now() });
        } }];
  }

  async onFormValueChanged(formValue: FormValue): Promise<void> {
    const products = this.store.get(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.getters.preparedEntities
    );
    const productsArray = Object.values(products);
    console.log(formValue);
    this.formValue = formValue;
    await Promise.all(
      productsArray.map((product) => this.prepareProduct(product as Product, formValue))
    );
  }

  async onValidFromChange(validFrom: string): Promise<void> {
    await this.loadProducts(validFrom);
  }

  async onRowInputChange(
    product: Product,
    changes: Partial<Product>,
    formValue: FormValue
  ) {
    const updatedProduct = { ...product, ...changes };
    await this.prepareProduct(updatedProduct, formValue);
  }

  toggleSelected(product: Product): void {
    const currentlySelected = this.selected$.getValue();

    if (currentlySelected.id !== null && currentlySelected.id === product.id)
      this.selected$.next({ id: null });
    else this.selected$.next({ id: product.id });
  }

  async showPrintConfigDialog(data: { valid_from: string }): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<PrintPayload | null, { valid_from: string }>(),
      {
        id: 'KONDITIONSLISTE_PRINT_CONFIG_DIALOG',
        component: PrintConfigDialogComponent,
        dismiss: true,
        data: {
          valid_from: data.valid_from } }
    );

    if (!result || result === 'DISMISSED' || result === null) return;

    try {
      result.valid_from = ListProductsComponent.formatDate(result.valid_from) ?? '';

      const downloadResult = await this.store.dispatch(
        SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
        KundenKonditionslisteStore.actions.print,
        result as PrintPayload
      );

      await this.fileService.openBlob(downloadResult.content, downloadResult.filename);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#KUNDEN_KONDITIONSLISTE.MESSAGES.PRINT.SUCCESS.TITLE' }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async onFormSubmit(value: FormSubmit): Promise<void> {
    if (!value.valid) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          message: 'Bitte überprüfen Sie die Eingaben.',
          title: `${!value.validFromValid ? 'Gültig ab' : 'Neues Preisdatum'} ist nicht korrekt ausgefüllt!` }
      );

      return;
    }

    const preparedEntities = this.store.get(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.getters.preparedEntities
    );
    const preparedProducts = (
      isDefined(preparedEntities) ? Object.values(preparedEntities) : []
    ) as Product[];

    if (!preparedProducts.length || !this.formValue) return;

    const allProducts = this.store.get(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.getters.all
    );

    // In this array we combine the updated products and all non-changed products
    // We need to do this, because we always should send all items to the API
    // because we don't know if we create a new sheet or just update an existing one
    const products: Product[] = [];

    if (allProducts.length >= preparedProducts.length) {
      for (const prod of allProducts) {
        const preparedProduct = preparedProducts.find((o) => o.id === prod.id);

        if (preparedProduct) {
          products.push(preparedProduct);
        } else {
          products.push(this.updateProductLocally(prod, this.formValue));
        }
      }
    } else {
      console.log('ERROR - This should not have happened!!!!');
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          title: 'Fehler!',
          duration: 5000 }
      );
      return;
    }

    try {
      const promises = products?.map((product) => {
        return this.store.dispatch(
          SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
          KundenKonditionslisteStore.actions.save,
          product
        );
      });

      await Promise.all(promises);

      await this.presentDoneMessage('success');
      //console.log(this.inputs?.toArray());
      this.inputs?.forEach((o) => {
        o.onInput(null);
        o.setValue(null);
        o.writeValue(null);
        o.value.set(null);
      });
    } catch (e: any) {
      if (e instanceof RequestError) {
        await this.presentDoneMessage('failure');
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  private async prepareProduct(product: Product, formValue: FormValue): Promise<void> {
    const changes = this.store.get(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.getters.changes
    );

    if (
      changes &&
      changes?.length &&
      !!(changes as CreateChange<Product>[])?.find((o) => o.entity.id === product.id)
    )
      this.store.commit(
        SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
        KundenKonditionslisteStore.mutations.resetChange,
        product.id
      );

    if (isNaN(+product?.price)) return;

    const updatedProduct = this.updateProductLocally(product, formValue);

    await this.store.dispatch(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.actions.prepareCreate,
      updatedProduct
    );
  }

  private updateProductLocally(product: Product, formValue: FormValue): Product {
    const updatedProduct: Product = {
      ...product,
      price: +product?.price };

    if (isNaN(+product?.price)) return product;

    const validFrom = ListProductsComponent.formatDate(formValue.validFrom);
    const newPriceDate = ListProductsComponent.formatDate(formValue.newPriceDate);

    if (validFrom) updatedProduct.valid_from = validFrom;

    if (newPriceDate) updatedProduct.valid_until = newPriceDate;

    return updatedProduct;
  }

  private loadProducts(validFrom: string): Promise<Product[]> {
    // reset the changes as there will potentially be other products
    this.store.commit(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.mutations.resetChanges
    );

    return this.store.dispatch(
      SOFTLINE_FEATURE_KUNDEN_KONDITIONSLISTE,
      KundenKonditionslisteStore.actions.loadMany,
      {
        clear: true,
        queryParams: {
          valid_from: ListProductsComponent.formatDate(validFrom) } }
    );
  }

  private async presentDoneMessage(type: 'success' | 'failure'): Promise<void> {
    if (type === 'success') {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#KUNDEN_KONDITIONSLISTE.MESSAGES.SUCCESS.TITLE',
          message: '#KUNDEN_KONDITIONSLISTE.MESSAGES.SUCCESS.MESSAGE' }
      );
    } else {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          title: '#KUNDEN_KONDITIONSLISTE.MESSAGES.ERROR.TITLE',
          message: '#KUNDEN_KONDITIONSLISTE.MESSAGES.ERROR.MESSAGE',
          duration: 5000 }
      );
    }
  }
}
